import {
    collection, doc, getDocs, getDoc, query, where, writeBatch,
    serverTimestamp, increment, runTransaction
} from "firebase/firestore";
import { db } from "../firebase";
import AuditService, { AUDIT_ACTIONS, AUDIT_MODULES } from "../audit_service";
import { toISODate } from "../../utils/date";
import { COLLECTIONS } from "../collections";
import { WEEKDAY_LABELS } from "../../constants/calendar";
import { TRIAL_ATTENDANCE_STATUS } from "../../constants/enrollments";
import { updateMemberActivities, notifyWaitlistOnVacancy } from "./helpers";

/**
 * Matrícula regular em uma ou mais turmas (Class) a partir de uma data.
 *
 * Para cada turma:
 * 1. Cria doc de matrícula em enrollments
 * 2. Adiciona o aluno ao array `attendances` de cada sessão futura
 * 3. Incrementa `enrolledCount` em cada sessão futura
 * 4. Incrementa `enrolledCount` na turma (class)
 */
export const enrollMember = async (idTenant, user, options) => {
    const { idMember, idMembership, memberName } = options;

    const fallbackStartDate = options.startDate || toISODate(new Date());
    let selectedClasses = options.selectedClasses || (options.classIds || []).map(id => ({ idClass: id, startDate: fallbackStartDate }));

    // Deduplicar por idClass para prevenir transações duplicadas
    const uniqueClasses = [];
    const seenClassIds = new Set();
    for (const sc of selectedClasses) {
        if (!seenClassIds.has(sc.idClass)) {
            seenClassIds.add(sc.idClass);
            uniqueClasses.push(sc);
        }
    }
    selectedClasses = uniqueClasses;

    if (!idTenant || !idMember || selectedClasses.length === 0) {
        throw new Error("Parâmetros incompletos para matrícula");
    }

    try {
        const enrollmentsRef = collection(db, "tenants", idTenant, COLLECTIONS.ENROLLMENTS);
        const sessionsRef = collection(db, "tenants", idTenant, COLLECTIONS.SESSIONS);

        // Verificar limites do contrato (fora da transação — limitação do Web SDK)
        let currentEnrollmentCount = 0;
        let contractLimits = null;
        if (idMembership) {
            const mmRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBER_MEMBERSHIPS, idMembership);
            const mmSnap = await getDoc(mmRef);
            if (mmSnap.exists()) {
                contractLimits = mmSnap.data();
                const memberEnrollmentsQuery = query(
                    enrollmentsRef,
                    where("idMember", "==", idMember),
                    where("idMembership", "==", idMembership),
                    where("status", "==", "active"),
                    where("type", "==", "regular")
                );
                const currentEnrollmentsSnap = await getDocs(memberEnrollmentsQuery);
                currentEnrollmentCount = currentEnrollmentsSnap.size;
            } else {
                // Na primeira venda o member_membership ainda não existe — buscar template do plano
                const membershipRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBERSHIPS, idMembership);
                const membershipSnap = await getDoc(membershipRef);
                if (membershipSnap.exists()) {
                    contractLimits = membershipSnap.data();
                }
            }
        }

        // Coletar refs das sessões futuras afetadas
        const sessionRefsToUpdate = [];
        for (const item of selectedClasses) {
            const classStartDateStr = item.startDate || fallbackStartDate;
            const sessionsQuery = query(
                sessionsRef,
                where("idClass", "==", item.idClass),
                where("sessionDate", ">=", classStartDateStr)
            );
            const sessionsSnap = await getDocs(sessionsQuery);
            sessionsSnap.docs.forEach(sessionDoc => sessionRefsToUpdate.push(sessionDoc.ref));
        }

        await runTransaction(db, async (t) => {
            const memberRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBERS, idMember);

            const memberSnap = await t.get(memberRef);
            let memberStatus = memberSnap.exists() ? memberSnap.data().status || 'active' : 'active';

            if (!options.skipStatusCheck && (memberStatus === 'inactive' || memberStatus === 'lead')) {
                throw new Error("Aluno inativo ou lead só pode ser agendado para aula única (Experimental). Utilize a opção correta.");
            }

            const classesData = {};
            for (const item of selectedClasses) {
                const classRef = doc(db, "tenants", idTenant, COLLECTIONS.CLASSES, item.idClass);
                const classSnap = await t.get(classRef);
                if (classSnap.exists()) {
                    classesData[item.idClass] = { ref: classRef, data: classSnap.data() };
                }
            }

            if (contractLimits) {
                const maxSessions = contractLimits.maxSessionsPerWeek || 0;
                const allowedDays = contractLimits.allowedDaysOfWeek || [];

                if (maxSessions > 0) {
                    if (currentEnrollmentCount + selectedClasses.length > maxSessions) {
                        throw new Error(`Limite do plano excedido! Cota de ${maxSessions} horários/sem. (Já possui ${currentEnrollmentCount}).`);
                    }
                }

                if (allowedDays.length > 0) {
                    for (const item of selectedClasses) {
                        const classData = classesData[item.idClass]?.data;
                        if (classData && classData.weekday !== undefined && classData.weekday !== null) {
                            if (!allowedDays.includes(classData.weekday)) {
                                throw new Error(`Contrato atual não permite acesso à grade neste dia: ${WEEKDAY_LABELS[classData.weekday]}.`);
                            }
                        }
                    }
                }
            }

            for (const item of selectedClasses) {
                const classObj = classesData[item.idClass];
                if (!classObj) continue;
                const cd = classObj.data;
                const maxCap = Number(cd.maxCapacity) || 0;
                const currentEnrolled = Number(cd.enrolledCount) || 0;
                if (maxCap > 0 && currentEnrolled >= maxCap) {
                    throw new Error(`TURMA_LOTADA:${item.idClass}:${cd.activityName || 'Turma'}`);
                }
            }

            const sessionsData = [];
            for (const ref of sessionRefsToUpdate) {
                const snap = await t.get(ref);
                if (snap.exists()) {
                    sessionsData.push({ ref: ref, data: snap.data() });
                }
            }

            for (const item of selectedClasses) {
                const idClass = item.idClass;
                const classObj = classesData[idClass];
                if (!classObj) continue;

                const classData = classObj.data;
                const classRef = classObj.ref;
                const classStartDateStr = item.startDate || fallbackStartDate;

                const newEnrollmentRef = doc(enrollmentsRef);
                t.set(newEnrollmentRef, {
                    idMember,
                    memberName,
                    memberEmail: memberSnap.exists() ? memberSnap.data().email || "" : "",
                    memberPhone: memberSnap.exists() ? memberSnap.data().phone || "" : "",
                    memberPhotoUrl: memberSnap.exists() ? memberSnap.data().photoUrl || "" : "",
                    idClass,
                    idMembership: idMembership || null,
                    type: "regular",
                    startDate: classStartDateStr,
                    status: "active",
                    idActivity: classData.idActivity || "",
                    activityName: classData.activityName || "",
                    activityColor: classData.activityColor || "#3b82f6",
                    weekday: classData.weekday ?? null,
                    weekdayLabel: WEEKDAY_LABELS[classData.weekday] || "",
                    startTime: classData.startTime || "",
                    endTime: classData.endTime || "",
                    idEmployee: classData.idEmployee || "",
                    employeeName: classData.employeeName || "",
                    employeeEmail: classData.employeeEmail || "",
                    employeePhone: classData.employeePhone || "",
                    employeePhotoUrl: classData.employeePhotoUrl || "",
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                    createdBy: {
                        uid: user?.uid || 'system',
                        name: user?.displayName || user?.name || user?.email || 'Sistema'
                    }
                });

                t.update(classRef, {
                    enrolledCount: increment(1),
                    updatedAt: serverTimestamp()
                });

                const filteredSessions = sessionsData.filter(
                    s => s.data.idClass === idClass && s.data.sessionDate >= classStartDateStr
                );

                for (const session of filteredSessions) {
                    const currentAttendances = session.data.attendances || [];

                    if (!currentAttendances.some(a => a.idMember === idMember)) {
                        currentAttendances.push({
                            idEnrollment: newEnrollmentRef.id,
                            idMember,
                            memberName,
                            type: "REGULAR",
                            status: "BOOKED"
                        });

                        const regularCount = currentAttendances.filter(a => a.type === "REGULAR").length;
                        const trialCount = currentAttendances.filter(a => a.type === "TRIAL").length;

                        t.update(session.ref, {
                            attendances: currentAttendances,
                            enrolledCount: regularCount,
                            experimentalCount: trialCount,
                            updatedAt: serverTimestamp()
                        });
                    }
                }
            }
        });

        await updateMemberActivities(idTenant, idMember);

        const auditClassDetails = [];
        for (const item of selectedClasses) {
            const classRef = doc(db, "tenants", idTenant, COLLECTIONS.CLASSES, item.idClass);
            const classSnap = await getDoc(classRef);
            const classData = classSnap.exists() ? classSnap.data() : {};
            auditClassDetails.push({
                turma: classData.activityName || item.idClass,
                dia: classData.weekdayLabel || WEEKDAY_LABELS[classData.weekday] || "-",
                horario: classData.startTime ? `${classData.startTime} - ${classData.endTime || ''}` : "-",
                professor: classData.employeeName || "-",
                inicio: item.startDate || fallbackStartDate,
            });
        }
        const turmaNames = auditClassDetails.map(c => c.turma).join(", ");

        AuditService.log({
            idTenant,
            user,
            module: AUDIT_MODULES.ENROLLMENTS,
            action: AUDIT_ACTIONS.CREATE,
            entityId: idMember,
            entityName: memberName,
            description: `Matriculou "${memberName}" em ${auditClassDetails.length} turma(s): ${turmaNames}`,
            details: { turmas: auditClassDetails, tipo: "regular", metodo: "transaction" }
        });

        return true;
    } catch (error) {
        console.error("[EnrollmentService] Erro na transação de matrícula:", error);
        throw error;
    }
};

/**
 * Matrícula de Pacote de Aulas — sessões avulsas com débito de créditos.
 *
 * 1. Cria doc de matrícula tipo "class-package" para cada sessão
 * 2. Adiciona o aluno ao array `attendances` de cada sessão selecionada
 * 3. Debita `remainingClasses` no memberMembership
 * 4. Se saldo chega a 0 → auto-expira o contrato
 */
export const enrollClassPackage = async (idTenant, user, options) => {
    const { idMember, idMembership, memberName, selectedSessions } = options;

    if (!idTenant || !idMember || !selectedSessions || selectedSessions.length === 0) {
        throw new Error("Parâmetros incompletos para matrícula de pacote de aulas.");
    }
    if (!idMembership) {
        throw new Error("Pacote de aulas requer um contrato (memberMembership) vinculado.");
    }

    try {
        const mmRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBER_MEMBERSHIPS, idMembership);
        const mmSnap = await getDoc(mmRef);
        if (!mmSnap.exists()) throw new Error("Contrato não encontrado.");

        const mm = mmSnap.data();
        if (mm.durationType !== 'classes') throw new Error("Contrato não é do tipo pacote de aulas.");

        const remainingClasses = mm.remainingClasses || 0;
        if (remainingClasses < selectedSessions.length) {
            throw new Error(`Saldo insuficiente! Restam ${remainingClasses} aula(s), mas ${selectedSessions.length} foram selecionadas.`);
        }

        const batch = writeBatch(db);
        const enrollmentsRef = collection(db, "tenants", idTenant, COLLECTIONS.ENROLLMENTS);

        const memberRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBERS, idMember);
        const memberSnap = await getDoc(memberRef);
        const memberData = memberSnap.exists() ? memberSnap.data() : {};

        for (const session of selectedSessions) {
            const sessionRef = doc(db, "tenants", idTenant, COLLECTIONS.SESSIONS, session.sessionId);
            const sessionSnap = await getDoc(sessionRef);

            if (!sessionSnap.exists()) continue;
            const sessionData = sessionSnap.data();

            const currentAttendances = sessionData.attendances || [];
            if (currentAttendances.some(a => a.idMember === idMember)) continue;

            const newEnrollmentRef = doc(enrollmentsRef);
            batch.set(newEnrollmentRef, {
                idMember,
                memberName,
                memberEmail: memberData.email || "",
                memberPhone: memberData.phone || "",
                memberPhotoUrl: memberData.photoUrl || "",
                idClass: sessionData.idClass || null,
                idMembership,
                sessionId: session.sessionId,
                sessionDate: sessionData.sessionDate,
                type: "class-package",
                status: "active",
                idActivity: sessionData.idActivity || "",
                activityName: sessionData.activityName || "",
                activityColor: sessionData.activityColor || "#3b82f6",
                weekday: sessionData.weekday ?? null,
                weekdayLabel: WEEKDAY_LABELS[sessionData.weekday] || "",
                startTime: sessionData.startTime || "",
                endTime: sessionData.endTime || "",
                idEmployee: sessionData.idEmployee || "",
                employeeName: sessionData.employeeName || "",
                employeeEmail: sessionData.employeeEmail || "",
                employeePhone: sessionData.employeePhone || "",
                employeePhotoUrl: sessionData.employeePhotoUrl || "",
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                createdBy: {
                    uid: user?.uid || 'system',
                    name: user?.displayName || user?.name || user?.email || 'Sistema'
                }
            });

            currentAttendances.push({
                idEnrollment: newEnrollmentRef.id,
                idMember,
                memberName,
                type: "CLASS_PACKAGE",
                status: "BOOKED"
            });

            const regularCount = currentAttendances.filter(a => a.type === "REGULAR").length;
            const trialCount = currentAttendances.filter(a => a.type === "TRIAL").length;
            const packageCount = currentAttendances.filter(a => a.type === "CLASS_PACKAGE").length;

            batch.update(sessionRef, {
                attendances: currentAttendances,
                enrolledCount: regularCount + packageCount,
                experimentalCount: trialCount,
                updatedAt: serverTimestamp()
            });
        }

        const newRemaining = remainingClasses - selectedSessions.length;
        const mmUpdates = {
            remainingClasses: increment(-selectedSessions.length),
            updatedAt: serverTimestamp()
        };
        if (newRemaining <= 0) {
            mmUpdates.status = 'expired';
        }
        batch.update(mmRef, mmUpdates);

        await batch.commit();

        AuditService.log({
            idTenant,
            user,
            module: AUDIT_MODULES.ENROLLMENTS,
            action: AUDIT_ACTIONS.CREATE,
            entityId: idMember,
            entityName: memberName,
            description: `Pacote de aulas: ${selectedSessions.length} sessão(ões) agendada(s) para "${memberName}". Saldo: ${remainingClasses} → ${newRemaining}.`,
            details: {
                tipo: "pacote-de-aulas",
                sessoes: selectedSessions.length,
                saldoAnterior: remainingClasses,
                saldoNovo: newRemaining
            }
        });

        return true;
    } catch (error) {
        console.error("[EnrollmentService] Erro ao agendar pacote de aulas:", error);
        throw error;
    }
};

/**
 * Agenda aula experimental em uma sessão específica.
 *
 * 1. Cria doc de matrícula tipo "trial" em enrollments
 * 2. Adiciona o aluno ao array `attendances` da sessão com type="TRIAL"
 * 3. Incrementa `experimentalCount` na sessão
 * 4. NÃO altera enrolledCount da turma — experimentais não contam como regulares
 */
export const scheduleTrialClass = async (idTenant, user, options) => {
    const { idMember, sessionId, memberName } = options;
    if (!idTenant || !idMember || !sessionId) {
        throw new Error("Parâmetros incompletos para aula experimental");
    }

    try {
        const batch = writeBatch(db);

        const enrollmentsRef = collection(db, "tenants", idTenant, COLLECTIONS.ENROLLMENTS);
        const newEnrollmentRef = doc(enrollmentsRef);

        const sessionRef = doc(db, "tenants", idTenant, COLLECTIONS.SESSIONS, sessionId);
        const sessionSnap = await getDoc(sessionRef);

        if (!sessionSnap.exists()) {
            throw new Error("Sessão não existe.");
        }

        const sessionData = sessionSnap.data();

        const memberRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBERS, idMember);
        const memberSnap = await getDoc(memberRef);

        if (!memberSnap.exists()) {
            throw new Error("Membro não encontrado.");
        }

        const memberData = memberSnap.data();

        const enrollmentData = {
            idMember,
            memberName,
            memberPhone: memberData.phone || "",
            memberEmail: memberData.email || "",
            memberPhotoUrl: memberData.photoUrl || "",
            idClass: sessionData.idClass || null,
            sessionId,
            sessionDate: sessionData.sessionDate,
            startTime: sessionData.startTime || "",
            endTime: sessionData.endTime || "",
            activityName: sessionData.activityName || "",
            idEmployee: sessionData.idEmployee || "",
            employeeName: sessionData.employeeName || "",
            employeeEmail: sessionData.employeeEmail || "",
            employeePhone: sessionData.employeePhone || "",
            employeePhotoUrl: sessionData.employeePhotoUrl || "",
            idConsultant: memberData.consultant?.uid || null,
            consultantName: memberData.consultant?.name || null,
            type: "trial",
            status: "active",
            attendanceStatus: TRIAL_ATTENDANCE_STATUS.PENDING,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            createdBy: {
                uid: user?.uid || 'system',
                name: user?.displayName || user?.name || user?.email || 'Sistema'
            }
        };

        batch.set(newEnrollmentRef, enrollmentData);

        const currentAttendances = sessionData.attendances || [];

        if (!currentAttendances.some(a => a.idMember === idMember)) {
            currentAttendances.push({
                idEnrollment: newEnrollmentRef.id,
                idMember,
                memberName,
                type: "TRIAL",
                status: "BOOKED"
            });

            const regularCount = currentAttendances.filter(a => a.type === "REGULAR").length;
            const trialCount = currentAttendances.filter(a => a.type === "TRIAL").length;

            batch.update(sessionRef, {
                attendances: currentAttendances,
                enrolledCount: regularCount,
                experimentalCount: trialCount,
                updatedAt: serverTimestamp()
            });
        } else {
            throw new Error("O aluno já está registrado nesta sessão.");
        }

        await batch.commit();

        AuditService.log({
            idTenant,
            user,
            module: AUDIT_MODULES.ENROLLMENTS,
            action: AUDIT_ACTIONS.CREATE,
            entityId: idMember,
            entityName: memberName,
            description: `Agendou aula experimental para "${memberName}" — ${sessionData.activityName || 'Atividade'} em ${sessionData.sessionDate || ''} (${sessionData.startTime || ''})`,
            details: {
                tipo: "experimental",
                atividade: sessionData.activityName || "-",
                data: sessionData.sessionDate || "-",
                horario: sessionData.startTime ? `${sessionData.startTime} - ${sessionData.endTime || ''}` : "-",
                professor: sessionData.employeeName || "-",
            }
        });

        return true;
    } catch (error) {
        console.error("[EnrollmentService] Erro ao agendar aula experimental:", error);
        throw error;
    }
};

/**
 * Cancela uma matrícula.
 *
 * 1. Marca o doc de enrollment como status="canceled"
 * 2. Remove o aluno do array `attendances` de sessões FUTURAS
 * 3. Recalcula enrolledCount e experimentalCount em cada sessão afetada
 * 4. Decrementa enrolledCount na turma (class) se era regular
 * NÃO afeta sessões passadas.
 */
export const cancelEnrollment = async (idTenant, user, enrollment) => {
    const { id: enrollmentId, idMember, idClass, type } = enrollment;
    if (!idTenant || !enrollmentId || !idMember) {
        throw new Error("Parâmetros incompletos para cancelar matrícula");
    }

    try {
        const todayStr = toISODate(new Date());
        const batch = writeBatch(db);

        const enrollmentRef = doc(db, "tenants", idTenant, COLLECTIONS.ENROLLMENTS, enrollmentId);
        batch.update(enrollmentRef, {
            status: "canceled",
            canceledAt: serverTimestamp(),
            canceledBy: user?.uid || 'system',
            updatedAt: serverTimestamp()
        });

        if (type === 'regular' && idClass) {
            const classRef = doc(db, "tenants", idTenant, COLLECTIONS.CLASSES, idClass);
            const classSnap = await getDoc(classRef);
            if (classSnap.exists()) {
                batch.update(classRef, {
                    enrolledCount: increment(-1),
                    updatedAt: serverTimestamp()
                });
            }
        }

        if (idClass && type === 'regular') {
            const sessionsRef = collection(db, "tenants", idTenant, COLLECTIONS.SESSIONS);
            const sessionsQuery = query(
                sessionsRef,
                where("idClass", "==", idClass),
                where("sessionDate", ">=", todayStr)
            );
            const sessionsSnap = await getDocs(sessionsQuery);

            sessionsSnap.docs.forEach(sessionDoc => {
                const sessionData = sessionDoc.data();
                const currentAttendances = sessionData.attendances || [];
                const updatedAttendances = currentAttendances.filter(a => a.idMember !== idMember);

                if (updatedAttendances.length !== currentAttendances.length) {
                    const regularCount = updatedAttendances.filter(a => a.type === "REGULAR").length;
                    const trialCount = updatedAttendances.filter(a => a.type === "TRIAL").length;

                    batch.update(sessionDoc.ref, {
                        attendances: updatedAttendances,
                        enrolledCount: regularCount,
                        experimentalCount: trialCount,
                        updatedAt: serverTimestamp()
                    });
                }
            });
        } else if (enrollment.sessionId && type === 'trial') {
            const sessionRef = doc(db, "tenants", idTenant, COLLECTIONS.SESSIONS, enrollment.sessionId);
            const sessionSnap = await getDoc(sessionRef);

            if (sessionSnap.exists()) {
                const sessionData = sessionSnap.data();
                const sessionDate = sessionData.sessionDate;

                if (sessionDate >= todayStr) {
                    const currentAttendances = sessionData.attendances || [];
                    const updatedAttendances = currentAttendances.filter(a => a.idMember !== idMember);

                    if (updatedAttendances.length !== currentAttendances.length) {
                        const regularCount = updatedAttendances.filter(a => a.type === "REGULAR").length;
                        const trialCount = updatedAttendances.filter(a => a.type === "TRIAL").length;

                        batch.update(sessionRef, {
                            attendances: updatedAttendances,
                            enrolledCount: regularCount,
                            experimentalCount: trialCount,
                            updatedAt: serverTimestamp()
                        });
                    }
                }
            }
        } else if (enrollment.sessionId && type === 'class-package') {
            const sessionRef = doc(db, "tenants", idTenant, COLLECTIONS.SESSIONS, enrollment.sessionId);
            const sessionSnap = await getDoc(sessionRef);

            if (sessionSnap.exists()) {
                const sessionData = sessionSnap.data();
                const currentAttendances = sessionData.attendances || [];
                const memberAttendance = currentAttendances.find(a => a.idMember === idMember);
                const wasBooked = memberAttendance && memberAttendance.status === "BOOKED";

                const updatedAttendances = currentAttendances.filter(a => a.idMember !== idMember);

                if (updatedAttendances.length !== currentAttendances.length) {
                    const regularCount = updatedAttendances.filter(a => a.type === "REGULAR").length;
                    const trialCount = updatedAttendances.filter(a => a.type === "TRIAL").length;
                    const packageCount = updatedAttendances.filter(a => a.type === "CLASS_PACKAGE").length;

                    batch.update(sessionRef, {
                        attendances: updatedAttendances,
                        enrolledCount: regularCount + packageCount,
                        experimentalCount: trialCount,
                        updatedAt: serverTimestamp()
                    });
                }

                // Estornar crédito se a sessão ainda não foi chamada
                if (wasBooked && enrollment.idMembership) {
                    const mmRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBER_MEMBERSHIPS, enrollment.idMembership);
                    batch.update(mmRef, {
                        remainingClasses: increment(1),
                        updatedAt: serverTimestamp()
                    });
                }
            }
        }

        await batch.commit();

        if (type === 'regular') {
            await updateMemberActivities(idTenant, idMember);
        }

        AuditService.log({
            idTenant,
            user,
            module: AUDIT_MODULES.ENROLLMENTS,
            action: AUDIT_ACTIONS.UPDATE,
            entityId: enrollmentId,
            entityName: enrollment.memberName || idMember,
            description: `Cancelou matrícula ${type === 'trial' ? 'experimental' : 'regular'} de "${enrollment.memberName || 'Aluno'}" — ${enrollment.activityName || 'Atividade'}`,
            details: {
                aluno: enrollment.memberName || idMember,
                tipo: type === 'trial' ? 'Experimental' : 'Regular',
                atividade: enrollment.activityName || "-",
                dia: enrollment.weekdayLabel || "-",
                horario: enrollment.startTime ? `${enrollment.startTime} - ${enrollment.endTime || ''}` : "-",
            }
        });

        if (type === 'regular' && idClass) {
            notifyWaitlistOnVacancy(idTenant, user, idClass, enrollment).catch(err => {
                console.warn("[EnrollmentService] Erro ao notificar lista de espera (não-crítico):", err);
            });
        }

        return true;
    } catch (error) {
        console.error("[EnrollmentService] Erro ao cancelar matrícula:", error);
        throw error;
    }
};
